import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Alert, Typography } from '@mui/material';
import api from '../services/api';

const ProfileContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const ProfileCard = styled.div`
  background-color: white;
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: ${props => props.theme.shadows.medium};
  padding: 30px;
  margin-bottom: 30px;
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: bold;
  margin-right: 20px;
`;

const ProfileInfo = styled.div`
  flex-grow: 1;
`;

const Username = styled.h1`
  font-size: 24px;
  margin-bottom: 5px;
`;

const Email = styled.p`
  font-size: 16px;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 5px;
`;

const JoinDate = styled.p`
  font-size: 14px;
  color: ${props => props.theme.colors.secondary};
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const StatCard = styled.div`
  background-color: ${props => props.theme.colors.highlight};
  border-radius: ${props => props.theme.borderRadius};
  padding: 15px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 5px;
  color: ${props => props.theme.colors.primary};
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${props => props.theme.colors.secondary};
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  margin-bottom: 15px;
  margin-top: 30px;
`;

const ActionButton = styled(Link)`
  display: inline-block;
  padding: 10px 20px;
  margin-right: 10px;
  margin-bottom: 10px;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border-radius: ${props => props.theme.borderRadius};
  text-decoration: none;
  font-weight: bold;
  
  &:hover {
    background-color: #0062c1;
  }
`;

const DangerButton = styled(Button)`
  && {
    background-color: #d32f2f;
    color: white;
    &:hover { background-color: #b71c1c; }
    margin-right: 10px;
    margin-bottom: 10px;
  }
`;

function Profile({ user }) {
  const [userStats, setUserStats] = useState({
    movieCount: 0,
    averageRating: 0,
    kriticMatches: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);

        // Get user's rated movies (this endpoint returns all interactions, including dismissed/wanna watch)
        const allInteractions = await api.getUserRatings(user.id);
        const ratedMovies = allInteractions.filter(m => m.rating > 0);

        // Get user's kritic matches
        const kritics = await api.getMatchingKritics(user.id);

        // Calculate stats
        const movieCount = ratedMovies.length;
        const averageRating = ratedMovies.length > 0
          ? ratedMovies.reduce((sum, movie) => sum + movie.rating, 0) / ratedMovies.length
          : 0;

        setUserStats({
          movieCount,
          averageRating,
          kriticMatches: kritics.length
        });
      } catch (error) {
        console.error('Failed to fetch user data', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [user.id]);

  const executeReset = async (action, name) => {
    if (!window.confirm(`Are you sure you want to reset ${name}? This cannot be undone.`)) return;
    try {
      await action(user.id);
      setFeedback({ msg: `${name} reset successfully.`, type: 'success' });
      // For full reset, we might want to refresh stats immediately, but we rely on next reload
    } catch (error) {
      setFeedback({ msg: error.message, type: 'error' });
    }
  };

  // Get first letter of username for avatar
  const avatarLetter = user.username.charAt(0).toUpperCase();

  // Format join date
  const joinDate = user.created_at
    ? new Date(user.created_at).toLocaleDateString()
    : 'Unknown';

  if (isLoading) {
    return <ProfileContainer>Loading profile...</ProfileContainer>;
  }

  return (
    <ProfileContainer>
      <ProfileCard>
        <ProfileHeader>
          <Avatar>{avatarLetter}</Avatar>
          <ProfileInfo>
            <Username>{user.username}</Username>
            {user.email && <Email>{user.email}</Email>}
            <JoinDate>Joined: {joinDate}</JoinDate>
          </ProfileInfo>
        </ProfileHeader>

        <StatsContainer>
          <StatCard>
            <StatValue>{userStats.movieCount}</StatValue>
            <StatLabel>Movies Rated</StatLabel>
          </StatCard>

          <StatCard>
            <StatValue>{userStats.averageRating.toFixed(1)}</StatValue>
            <StatLabel>Average Rating</StatLabel>
          </StatCard>

          <StatCard>
            <StatValue>{userStats.kriticMatches}</StatValue>
            <StatLabel>Kritic Matches</StatLabel>
          </StatCard>
        </StatsContainer>
      </ProfileCard>

      {feedback && <Alert severity={feedback.type} onClose={() => setFeedback(null)} sx={{ mb: 3 }}>{feedback.msg}</Alert>}

      <SectionTitle>Quick Actions</SectionTitle>

      <div>
        <ActionButton to="/my-movies">My Movies</ActionButton>
        <ActionButton to="/kritics">View Kritic Matches</ActionButton>
        <ActionButton to="/import/letterboxd">Import from Letterboxd</ActionButton>
      </div>

      <SectionTitle>Data Administration</SectionTitle>
      <Typography variant="body2" color="error" paragraph>
        Warning: These actions are destructive and cannot be undone.
      </Typography>

      <DangerButton variant="contained" onClick={() => executeReset(api.resetAllData, "ALL DATA")}>Reset Everything</DangerButton>
      <Button variant="outlined" color="error" onClick={() => executeReset(api.resetRatings, "Ratings")} sx={{ mr: 1, mb: 1 }}>Reset Ratings</Button>
      <Button variant="outlined" color="error" onClick={() => executeReset(api.resetWatchlist, "Watchlist")} sx={{ mr: 1, mb: 1 }}>Reset Watchlist</Button>
      <Button variant="outlined" color="error" onClick={() => executeReset(api.resetDismissed, "Dismissed Movies")} sx={{ mr: 1, mb: 1 }}>Reset Dismissed</Button>

    </ProfileContainer>
  );
}

export default Profile; 